<template>
  <div class="question-bindings-editor">
    <!-- Слоты со своими чипами -->
    <div
      v-for="slot in slots"
      :key="slot.code"
      class="mb-1"
    >
      <div class="font-weight-bold small mb-25">
        {{ slot.name }}
        <span v-if="slot.required" class="text-danger" title="Обязательный слот">*</span>
      </div>

      <!-- Текстовый слот -->
      <b-form-input
        v-if="slot.entity === 'text'"
        :value="slotValue(slot.code)"
        :disabled="readonly"
        size="sm"
        placeholder="Текст"
        @input="onSlotValueInput(slot.code, $event)"
      />

      <template v-else>
        <div class="d-flex flex-wrap align-items-center">
          <span
            v-for="(item, itemIndex) in slotItems(slot.code)"
            :key="`${slot.code}-${itemIndex}`"
            class="mr-50 mb-50"
          >
            <b-badge :variant="isUnresolved(item) ? 'light-danger' : 'light-success'" class="chip">
              {{ item.name || `#${item.id}` }}
              <b-link
                v-if="isUnresolved(item) && !readonly"
                class="ml-50 text-danger"
                @click="startSearch(slot.code, itemIndex)"
              >найти</b-link>
              <b-link v-if="!readonly" class="ml-50 text-body" title="Убрать" @click="removeItem(slot.code, itemIndex)">×</b-link>
            </b-badge>
          </span>

          <b-button
            v-if="!readonly"
            variant="flat-primary"
            size="sm"
            class="btn-icon mb-50"
            :title="slot.multiple ? 'Добавить' : 'Заменить'"
            @click="startSearch(slot.code, null)"
          >
            <feather-icon icon="PlusIcon" size="14" />
          </b-button>
        </div>

        <!-- Поиск сущности: новое значение (index = null) или замена нерасшифрованного чипа -->
        <div v-if="searching && searching.slotCode === slot.code" class="search-box mb-50">
          <v-select
            v-if="slot.entity !== 'artist_cover'"
            :options="searchOptions"
            :filterable="false"
            label="label"
            placeholder="Начните вводить название"
            :loading="searchLoading"
            @search="(query, loading) => onSearch(slot.entity, query, loading)"
            @input="option => onPick(slot, option)"
          >
            <template #no-options>Ничего не найдено</template>
          </v-select>

          <template v-else>
            <v-select
              v-model="coverArtist"
              :options="searchOptions"
              :filterable="false"
              label="label"
              placeholder="Сначала артист"
              :loading="searchLoading"
              class="mb-50"
              @search="(query, loading) => onSearch('artist', query, loading)"
              @input="onCoverArtistPick"
            >
              <template #no-options>Ничего не найдено</template>
            </v-select>
            <v-select
              v-if="coverArtist"
              :options="coverOptions"
              label="label"
              placeholder="Обложка"
              :loading="coversLoading"
              @input="option => onPick(slot, option)"
            >
              <template #no-options>У артиста нет обложек</template>
            </v-select>
          </template>

          <b-button variant="flat-secondary" size="sm" class="mt-25" @click="cancelSearch">Отмена</b-button>
        </div>
      </template>
    </div>

    <!-- Не найденные при импорте, у которых нет чипа в слотах -->
    <div v-if="orphanUnresolved.length" class="mb-1">
      <div class="font-weight-bold small mb-25 text-danger">Не найдено в базе</div>
      <div class="d-flex flex-wrap align-items-center">
        <b-badge
          v-for="(entry, entryIndex) in orphanUnresolved"
          :key="`unresolved-${entryIndex}`"
          variant="light-danger"
          class="chip mr-50 mb-50"
        >
          {{ entry.name }}
          <b-link v-if="!readonly" class="ml-50 text-danger" @click="startOrphanSearch(entry)">найти</b-link>
        </b-badge>
      </div>
    </div>

    <!-- Декады, кластеры, происхождение -->
    <b-row>
      <b-col md="4" class="mb-50">
        <label class="small mb-25">Декады</label>
        <v-select
          :value="selectedDecades"
          :options="decadeOptions"
          label="name"
          multiple
          :disabled="readonly"
          :close-on-select="false"
          placeholder="Декады"
          @input="onDecadesInput"
        />
      </b-col>
      <b-col md="5" class="mb-50">
        <label class="small mb-25">Кластеры</label>
        <v-select
          :value="selectedClusters"
          :options="clusterOptions"
          label="title"
          multiple
          :disabled="readonly"
          :close-on-select="false"
          placeholder="Кластеры"
          @input="onClustersInput"
        />
      </b-col>
      <b-col md="3" class="mb-50">
        <label class="small mb-25">Происхождение</label>
        <v-select
          :value="selectedOrigin"
          :options="originOptions"
          label="name"
          :disabled="readonly"
          placeholder="Не указано"
          @input="onOriginInput"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BBadge, BButton, BCol, BFormInput, BLink, BRow,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { computed, ref, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'

export default {
  components: {
    BBadge, BButton, BCol, BFormInput, BLink, BRow, vSelect,
  },
  props: {
    // [{code, name, entity, multiple, required, counts_as_played}]
    slots: { type: Array, default: () => [] },
    // {bindings: {slotCode: [{entity, id, name}]}, decades: [int], cluster_ids: [int], origin: code|null, slot_values: {slotCode: string}}
    value: { type: Object, default: () => ({}) },
    // [{entity, name}] — не найденные при импорте
    unresolved: { type: Array, default: () => [] },
    // {decades: [{id,name,value}], clusters: [{id,title}], origins: [{id,name,code}]}
    lookups: { type: Object, default: () => ({}) },
    readonly: { type: Boolean, default: false },
    // Идентичность вопроса: при смене запоздавшие ответы с метками отбрасываются
    contextKey: { type: [String, Number], default: null },
  },
  setup(props, { emit }) {
    const MODULE = 'question-storage'
    const toast = useToast()
    const entityTitles = {
      artist: 'артистов', track: 'треков', movie: 'фильмов', artist_cover: 'обложек',
    }

    // { slotCode, index (замена чипа или null), orphan (запись unresolved или null) }
    const searching = ref(null)
    const searchOptions = ref([])
    const searchLoading = ref(false)
    const coverArtist = ref(null)
    const coverOptions = ref([])
    const coversLoading = ref(false)
    let searchRequest = 0
    // Счётчик смены вопроса: ответ меток, запрошенный для прежнего вопроса, не применяется
    let tagsEpoch = 0
    watch(() => props.contextKey, () => { tagsEpoch += 1 })

    const current = computed(() => ({
      bindings: props.value.bindings || {},
      decades: props.value.decades || [],
      cluster_ids: props.value.cluster_ids || [],
      origin: props.value.origin || null,
      slot_values: props.value.slot_values || {},
    }))

    const emitValue = patch => emit('input', { ...current.value, ...patch })

    const slotItems = code => current.value.bindings[code] || []
    const slotValue = code => current.value.slot_values[code] || ''
    const isUnresolved = item => item.id === null || item.id === undefined

    const decadeOptions = computed(() => props.lookups.decades || [])
    const clusterOptions = computed(() => props.lookups.clusters || [])
    const originOptions = computed(() => props.lookups.origins || [])

    const selectedDecades = computed(() => decadeOptions.value.filter(decade => current.value.decades.includes(decade.value)))
    const selectedClusters = computed(() => clusterOptions.value.filter(cluster => current.value.cluster_ids.includes(cluster.id)))
    const selectedOrigin = computed(() => originOptions.value.find(origin => origin.code === current.value.origin) || null)

    // Записи unresolved, для которых в привязках нет красного чипа
    const orphanUnresolved = computed(() => {
      const chipKeys = new Set()
      Object.values(current.value.bindings).forEach(items => {
        items.forEach(item => {
          if (isUnresolved(item)) chipKeys.add(`${item.entity}|${item.name}`)
        })
      })

      return (props.unresolved || []).filter(entry => !chipKeys.has(`${entry.entity}|${entry.name}`))
    })

    const resetSearch = () => {
      searchOptions.value = []
      coverArtist.value = null
      coverOptions.value = []
    }

    const startSearch = (slotCode, index) => {
      resetSearch()
      searching.value = { slotCode, index, orphan: null }
    }

    // Слот для записи без чипа — первый слот с подходящей сущностью
    // Не затираем заполненный одиночный слот: берём пустой или множественный
    const startOrphanSearch = entry => {
      const matching = props.slots.filter(candidate => candidate.entity === entry.entity)
      const slot = matching.find(candidate => candidate.multiple || !slotItems(candidate.code).length)
      if (!slot) {
        toast({
          component: ToastificationContent,
          props: { title: 'Нет свободного слота для этой сущности — уберите значение вручную', icon: 'AlertTriangleIcon', variant: 'warning' },
        })
        return
      }
      resetSearch()
      searching.value = { slotCode: slot.code, index: null, orphan: entry }
    }

    const cancelSearch = () => {
      searching.value = null
      resetSearch()
    }

    const notifySearchError = (error, entity) => {
      const forbidden = error && error.response && error.response.status === 403
      toast({
        component: ToastificationContent,
        props: {
          title: forbidden ? `Нет прав на поиск: ${entityTitles[entity] || entity}` : 'Ошибка поиска',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }

    const onSearch = (entity, query, loading) => {
      if (!query || query.length < 2) {
        searchOptions.value = []
        return
      }
      searchRequest += 1
      const requestNumber = searchRequest
      loading(true)
      searchLoading.value = true

      let request
      if (entity === 'movie') {
        request = store.dispatch(`${MODULE}/searchMovies`, query)
          .then(response => (response.data.data || []).map(movie => ({ id: movie.id, label: movie.title_ru || movie.title_en })))
      } else if (entity === 'track') {
        request = store.dispatch(`${MODULE}/searchTracks`, { q: query })
          .then(response => (response.data.data || []).map(track => ({
            id: track.id,
            label: track.artist && !String(track.title).includes(track.artist) ? `${track.artist} - ${track.title}` : track.title,
          })))
      } else {
        request = store.dispatch(`${MODULE}/searchArtists`, query)
          .then(response => (response.data.data || []).map(artist => ({ id: artist.id, label: artist.title })))
      }

      request
        .then(options => {
          if (requestNumber === searchRequest) searchOptions.value = options
        })
        .catch(error => {
          if (requestNumber !== searchRequest) return
          searchOptions.value = []
          notifySearchError(error, entity)
        })
        .finally(() => {
          loading(false)
          searchLoading.value = false
        })
    }

    const onCoverArtistPick = option => {
      coverOptions.value = []
      if (!option) return
      coversLoading.value = true
      store.dispatch(`${MODULE}/fetchArtistCovers`, option.id)
        .then(response => {
          coverOptions.value = (response.data || []).map(cover => ({ id: cover.id, label: cover.name || `Обложка #${cover.id}` }))
        })
        .catch(error => {
          coverOptions.value = []
          notifySearchError(error, 'artist_cover')
        })
        .finally(() => { coversLoading.value = false })
    }

    const onPick = (slot, option) => {
      if (!option || !searching.value) return
      const { index, orphan } = searching.value
      const item = { entity: slot.entity, id: option.id, name: option.label }
      const items = [...slotItems(slot.code)]

      if (index !== null && index !== undefined) {
        items.splice(index, 1, item)
      } else if (slot.multiple) {
        items.push(item)
      } else {
        items.splice(0, items.length, item)
      }

      emitValue({ bindings: { ...current.value.bindings, [slot.code]: items } })
      if (orphan) emit('resolve', orphan)
      applyArtistTags(slot, option.id)
      cancelSearch()
    }

    // Метки исполнителя при ручном добавлении артиста в слот: объединение с текущими значениями, происхождение — только если пусто
    const applyArtistTags = (slot, artistId) => {
      if (slot.entity !== 'artist' || slot.counts_as_played === false || !artistId) return
      const requestEpoch = tagsEpoch
      store.dispatch(`${MODULE}/fetchArtistTags`, [artistId])
        .then(response => {
          // Вопрос сменился или артиста уже убрали из слота — метки не применяем
          if (requestEpoch !== tagsEpoch) return
          if (!slotItems(slot.code).some(item => item.id === artistId)) return
          const tags = ((response.data || {}).data || {})[artistId]
          if (!tags) return
          const decades = [...new Set([...current.value.decades, ...(tags.decades || [])])]
          const clusterIds = [...new Set([...current.value.cluster_ids, ...(tags.cluster_ids || [])])]
          emitValue({
            decades,
            cluster_ids: clusterIds,
            origin: current.value.origin || tags.origin || null,
          })
        })
        .catch(error => {
          const forbidden = error && error.response && error.response.status === 403
          toast({
            component: ToastificationContent,
            props: {
              title: forbidden ? 'Нет прав на получение меток исполнителя' : 'Не удалось получить метки исполнителя',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const removeItem = (slotCode, index) => {
      const items = [...slotItems(slotCode)]
      items.splice(index, 1)
      emitValue({ bindings: { ...current.value.bindings, [slotCode]: items } })
    }

    const onSlotValueInput = (slotCode, text) => {
      emitValue({ slot_values: { ...current.value.slot_values, [slotCode]: text } })
    }

    const onDecadesInput = list => emitValue({ decades: (list || []).map(decade => decade.value) })
    const onClustersInput = list => emitValue({ cluster_ids: (list || []).map(cluster => cluster.id) })
    const onOriginInput = origin => emitValue({ origin: origin ? origin.code : null })

    return {
      searching,
      searchOptions,
      searchLoading,
      coverArtist,
      coverOptions,
      coversLoading,
      slotItems,
      slotValue,
      isUnresolved,
      decadeOptions,
      clusterOptions,
      originOptions,
      selectedDecades,
      selectedClusters,
      selectedOrigin,
      orphanUnresolved,
      startSearch,
      startOrphanSearch,
      cancelSearch,
      onSearch,
      onCoverArtistPick,
      onPick,
      removeItem,
      onSlotValueInput,
      onDecadesInput,
      onClustersInput,
      onOriginInput,
    }
  },
}
</script>

<style scoped>
.chip {
  font-size: 0.9rem;
  padding: 0.35rem 0.6rem;
}
.search-box {
  max-width: 420px;
}
</style>
